import { IonContent } from '@ionic/react';
import React from 'react';

import { CompanyDetailsFooter } from '@/components/CompanyDetailsFooter';
import {
  PAYMENT_SECTION_ID,
  S3_BUCKET_URL,
  SECOND_SELECT_PLAN_SECTION_ID,
} from '@/constants';
import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { useAppModals } from '@/hooks/useAppModals';
import { CoinsPaywallModal } from '@/modals/CoinsPaywall';
import { webCampaign } from '@/modules/webCampaign';
import { useForestLayoutSelections } from '@/pages/Onboarding/hooks/useForestLayoutSelections';
import { useManLayoutSelections } from '@/pages/Onboarding/hooks/useManLayoutSelections';
import { useTMLayoutSelections } from '@/pages/Onboarding/hooks/useTMLayoutSelections';
import { useTransLayoutSelections } from '@/pages/Onboarding/hooks/useTransLayoutSelections';
import { FixedTimer } from '@/pages/Paywall/components/FixedTimer';
import { PaywallAINumber } from '@/pages/Paywall/sections/AINumber';
import { PaywallGuaranteePeriod } from '@/pages/Paywall/sections/GuaranteePeriod';
import { SectionPaymentMethodSelection } from '@/pages/Paywall/sections/PaymentMethodSelection';
import { SelectPlanComponentSelector } from '@/pages/Paywall/sections/SelectPlan/SelectPlanComponentSelector';
import { getTitleWithDialogue } from '@/utils/getTitleWithDialogue';
import { scrollToById } from '@/utils/scrollToById';

import { SectionMainImage } from '../V6/sections/MainImage';
import { SectionUsersReviews } from '../V6/sections/UsersReviews';

import {
  CompanyDetailsFooterWrapper,
  PaywallV7Styles,
  PaywallV7TitleStyles,
} from './FastPayTabsMan.styles';
import { SectionHowItWorks } from './sections/HowItWorks';
import { getImageByHisType } from './utils/getImageBySelection';

const PaywallFastPayTabsMan: React.FC = () => {
  const { ageGeneration, bodyShape, ethnicity } = useManLayoutSelections();
  const { bodyShape: forestBodyShape } = useForestLayoutSelections();
  const { his_type } = useTransLayoutSelections();
  const { bodyShape: tmBodyShape } = useTMLayoutSelections();

  const showFixedTimer =
    webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.FIXED_TIMER) ||
    webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.FIXED_TIMER_V2) ||
    webCampaign.setup.paywallNotes.includes(
      PAYWALL_NOTES.FIXED_TIMER_SECOND_PLAN,
    );
  const showPromoCode = webCampaign.setup.paywallNotes.includes('promo-code');
  const removeDialogueReferences = webCampaign.setup.paywallNotes.includes(
    PAYWALL_NOTES.REMOVE_DIALOGUE_REFERENCES,
  );
  const shouldShowCoinsPaywallModal = webCampaign.setup.paywallNotes.includes(
    PAYWALL_NOTES.SHOW_COINS_PAYWALL_MODAL,
  );

  const { show: showCoinsPaywallModal } = useAppModals('coinsPaywall');

  const getMainImageSrc = () => {
    if (webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.FOREST_IMAGES)) {
      return `${S3_BUCKET_URL}/paywall/forest/male/${forestBodyShape}.png`;
    }

    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.TM_MAXIM_PHOTOS)
    ) {
      return `${S3_BUCKET_URL}/paywall/fast-pay-tab-man/tm/maxim/${tmBodyShape}.jpg`;
    }

    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.TM_THOMAS_PHOTOS)
    ) {
      return `${S3_BUCKET_URL}/paywall/fast-pay-tab-man/tm/thomas/${tmBodyShape}.jpg`;
    }

    if (webCampaign.setup.paywallNotes.includes('PROD-2506')) {
      return `${S3_BUCKET_URL}/paywall/fast-pay-tab-man/PROD-2506/${ageGeneration}/${ethnicity}_${bodyShape}.png`;
    }

    if (webCampaign.setup.paywallNotes.includes('MAIN-697')) {
      return getImageByHisType(his_type);
    }

    if (webCampaign.setup.paywallNotes.includes('real-man-photos')) {
      return `${S3_BUCKET_URL}/paywall/fast-pay-tab-man/real-man-photos/${ethnicity}_${ageGeneration}_${bodyShape}.png`;
    }

    return `${S3_BUCKET_URL}/paywall/fast-pay-tab-man/${ageGeneration}/${ethnicity}_${bodyShape}.png`;
  };

  const promoCodeTitle = getTitleWithDialogue({
    removeDialogueReferences,
    isLure: webCampaign.isPaywallLure(),
    titleStyle: PaywallV7TitleStyles,
    withPromoCode: true,
  });

  const mainTitle = getTitleWithDialogue({
    removeDialogueReferences,
    isLure: webCampaign.isPaywallLure(),
    titleStyle: PaywallV7TitleStyles,
  });

  const getButtonProps = (scrollToPayment: () => void) => ({
    text: 'GET PREMIUM',
    onClick: shouldShowCoinsPaywallModal
      ? () => showCoinsPaywallModal()
      : scrollToPayment,
  });

  return (
    <PaywallV7Styles
      theme={webCampaign.getStaticTheme()}
      hasFixedTimer={showFixedTimer}
    >
      {showFixedTimer && <FixedTimer />}

      <IonContent>
        <SectionMainImage imageSrc={getMainImageSrc()} />

        <SelectPlanComponentSelector
          title={showPromoCode ? promoCodeTitle : null}
          vipTitle="One Day VIP"
          buttonProps={getButtonProps(() => scrollToById(PAYMENT_SECTION_ID))}
          isPromoCodeVisible={showPromoCode}
        />

        <PaywallAINumber />

        <SectionUsersReviews />

        <SectionHowItWorks />

        <SelectPlanComponentSelector
          title={mainTitle}
          vipTitle="One Day Premium"
          buttonProps={getButtonProps(() => scrollToById(PAYMENT_SECTION_ID))}
          isPromoCodeVisible={showPromoCode}
          sectionId={SECOND_SELECT_PLAN_SECTION_ID}
        />

        {!shouldShowCoinsPaywallModal && <SectionPaymentMethodSelection />}

        <PaywallGuaranteePeriod />
        {webCampaign.setup.paywallNotes.includes(
          PAYWALL_NOTES.SHOW_COMPANY_DETAILS_FOOTER,
        ) && (
          <CompanyDetailsFooterWrapper>
            <CompanyDetailsFooter />
          </CompanyDetailsFooterWrapper>
        )}
      </IonContent>

      {shouldShowCoinsPaywallModal && <CoinsPaywallModal />}
    </PaywallV7Styles>
  );
};

export default PaywallFastPayTabsMan;
